import { useQueryClient } from '@tanstack/react-query'
import type { UnifiedResource } from '@/services/resource.service'
import type { GitHubInstallation } from '@/shared/api/github-integrations.service'

// One key factory for readers and mutation invalidation.
export const resourceKeys = {
  models: ['resources', 'models'] as const,
  modelDefaults: ['resources', 'model-defaults'] as const,
  connections: ['resources', 'connections'] as const,
  /** The current Connections tab's nested list (PLAN-20260930 slice 9); never the pickers' flat list. */
  connectionsNested: ['resources', 'connections', 'nested'] as const,
  tools: ['resources', 'tools'] as const,
  agentHosts: ['resources', 'agent-hosts'] as const,
  providers: ['resources', 'providers', 'configured'] as const,
  modelAvailabilityAlerts: ['resources', 'model-availability-alerts'] as const,
  providerById: (id: string) => ['resources', 'provider', id] as const,
}

export function useInvalidateResourceCache() {
  const queryClient = useQueryClient()
  return {
    invalidateModels: () => queryClient.invalidateQueries({ queryKey: resourceKeys.models }),
    invalidateModelDefaults: () => queryClient.invalidateQueries({ queryKey: resourceKeys.modelDefaults }),
    invalidateConnections: () => queryClient.invalidateQueries({ queryKey: resourceKeys.connections }),
    invalidateTools: () => queryClient.invalidateQueries({ queryKey: resourceKeys.tools }),
    invalidateProviders: () => queryClient.invalidateQueries({ queryKey: resourceKeys.providers }),
    invalidateProvider: (providerId: string) =>
      queryClient.invalidateQueries({ queryKey: resourceKeys.providerById(providerId) }),
    // The selection has already persisted when this runs. A failed active
    // refetch must reach the save receipt; inactive queries remain invalidated
    // and ordinary invalidators keep their existing background behavior.
    invalidateProviderSave: (providerId?: string) => Promise.all([
      queryClient.invalidateQueries({ queryKey: resourceKeys.providers }, { throwOnError: true }),
      providerId
        ? queryClient.invalidateQueries({ queryKey: resourceKeys.providerById(providerId) }, { throwOnError: true })
        : Promise.resolve(),
      queryClient.invalidateQueries({ queryKey: resourceKeys.models }, { throwOnError: true }),
      queryClient.invalidateQueries({ queryKey: resourceKeys.modelDefaults }, { throwOnError: true }),
    ]),
    removeConnectionFromCache: (backendId: string) => {
      // The pickers' flat list and the Connections tab's nested list are separate entries.
      for (const key of [resourceKeys.connections, resourceKeys.connectionsNested]) {
        queryClient.setQueryData<{ resources?: UnifiedResource[]; githubInstallations?: GitHubInstallation[] } | undefined>(
          key,
          (prev) => {
            if (!prev || !Array.isArray(prev.resources)) return prev
            return {
              ...prev,
              resources: prev.resources.filter((resource) => {
                const resourceId = resource.id || resource.uri.split('/').pop()
                return resourceId !== backendId
              }),
            }
          },
        )
      }
    },
    /** Drop a deleted provider row without waiting for the list refetch. */
    removeProviderFromCache: (backendId: string) => {
      queryClient.setQueryData<{ providers?: Array<{ id: string }> } | undefined>(
        resourceKeys.providers,
        (prev) => {
          if (!prev || !Array.isArray(prev.providers)) return prev
          return { ...prev, providers: prev.providers.filter(p => p.id !== backendId) }
        },
      )
    },
  }
}
